import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  Skeleton,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute } from '@tanstack/react-router'
import { Download, FileText } from 'lucide-react'
import { useState } from 'react'

import { ApiError, errorMessage } from '@/api/errors'
import {
  useFiscalDocument,
  useFiscalDocuments,
  useFiscalExport,
  useIssueCreditNote,
} from '@/api/queries'
import type { FiscalDocument, FiscalDocumentKind } from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { formatMoney } from '@/lib/format'

/**
 * Il registro dei documenti fiscali — FAT-01, e l'export contabile di FAT-04.
 *
 * ## Perché non esisteva, e perché è una pagina e non una scheda dentro le vendite
 *
 * Le rotte erano in produzione dal 19 agosto e **nessuna schermata le chiamava**: un titolare
 * non poteva vedere cosa aveva emesso, e il suo commercialista non poteva ricevere niente.
 * Vedi §8bis del piano, «Le rotte che nessuno può raggiungere».
 *
 * Sta a sé perché la domanda che risolve è **del registro** e non della singola vendita:
 * «quali documenti ho emesso a marzo, su quale sezionale, e quanto fa». Da dentro una vendita
 * si emette (`FiscalSection` in `vendite`), da qui si legge il registro — sono due gesti di
 * due persone diverse in due momenti diversi.
 *
 * ## ⚠️ Il registro non è l'elenco delle vendite con un filtro
 *
 * Una vendita può non avere un documento, e una **nota di credito** non è una vendita.
 * Confonderli è il modo di far quadrare il fatturato con il registro per sbaglio: il primo
 * somma le vendite non annullate, il secondo i documenti emessi, e le due somme coincidono
 * solo quando ogni vendita è stata fatturata. La pagina lo scrive, perché è il genere di cosa
 * che si dà per scontata guardando due numeri vicini.
 *
 * ## L'anno è quello della **numerazione**
 *
 * Non `issueDate`: il numero riparte da 1 ogni anno e per sezionale, quindi «FT/2026/00001» si
 * cerca per anno di numerazione. Sono quasi sempre la stessa cosa e ogni tanto no — un
 * documento emesso il 2 gennaio con data 31 dicembre —, ed è esattamente il caso in cui uno
 * cerca.
 */
export const Route = createFileRoute('/_app/documenti/')({ component: FiscalDocumentsPage })

const KIND_LABELS: Record<FiscalDocumentKind, string> = {
  receipt: 'Ricevuta',
  invoice: 'Fattura',
  credit_note: 'Nota di credito',
}

function FiscalDocumentsPage() {
  const [kind, setKind] = useState<FiscalDocumentKind | ''>('')
  const [year, setYear] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [opened, setOpened] = useState<FiscalDocument | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)

  const parsedYear = /^\d{4}$/.test(year) ? Number(year) : undefined
  const documents = useFiscalDocuments({
    ...(kind === '' ? {} : { kind }),
    ...(parsedYear === undefined ? {} : { year: parsedYear }),
    ...(from === '' ? {} : { from }),
    ...(to === '' ? {} : { to }),
  })
  const exporter = useFiscalExport()

  const rows = documents.data?.data ?? []

  return (
    <>
      <PageHeader
        title="Documenti fiscali"
        description="Ricevute, fatture e note di credito emesse. Il registro, non l’elenco delle vendite."
        actions={
          <Button
            variant="outline"
            disabled={exporter.isPending || from === '' || to === ''}
            onClick={() => {
              setExportError(null)
              void exporter.mutateAsync({ from, to }).catch((caught: unknown) => {
                setExportError(
                  caught instanceof ApiError ? errorMessage(caught) : 'Export non riuscito.',
                )
              })
            }}
          >
            <Download className="size-4" /> Export contabile
          </Button>
        }
      />

      <div className="mb-4 grid gap-2 sm:grid-cols-4">
        <Field label="Tipo">
          {(props) => (
            <Select
              {...props}
              value={kind}
              onValueChange={(value) => setKind(value as FiscalDocumentKind | '')}
              items={[
                { value: '', label: 'Tutti' },
                ...Object.entries(KIND_LABELS).map(([value, label]) => ({ value, label })),
              ]}
            />
          )}
        </Field>
        {/* L'anno è quello della **numerazione**, non di `issueDate`. */}
        <Field label="Anno di numerazione">
          {(props) => (
            <Input
              {...props}
              inputMode="numeric"
              placeholder="2026"
              value={year}
              onChange={(event) => setYear(event.target.value)}
            />
          )}
        </Field>
        <Field label="Emessi dal">
          {(props) => (
            <Input
              {...props}
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          )}
        </Field>
        <Field label="al">
          {(props) => (
            <Input
              {...props}
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
          )}
        </Field>
      </div>

      {/*
        L'export vuole una finestra, e la pagina lo dice invece di lasciare il pulsante
        spento senza spiegazione: un file di cui nessuno sa dire cosa contiene è peggio di un
        file che non c'è.
      */}
      {from === '' || to === '' ? (
        <p className="text-muted-foreground mb-4 text-xs">
          Per l’export contabile scegli il periodo: il file esce con una riga per documento e per
          aliquota, che è la forma in cui un registro IVA si registra.
        </p>
      ) : null}
      {exportError === null ? null : (
        <Alert variant="danger" className="mb-4">
          {exportError}
        </Alert>
      )}

      {documents.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : documents.isError ? (
        <ErrorState
          message={errorMessage(documents.error)}
          requestId={documents.error instanceof ApiError ? documents.error.requestId : undefined}
          onRetry={() => void documents.refetch()}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<FileText />}
          title="Nessun documento emesso"
          description="Le ricevute e le fatture si emettono dal dettaglio di una vendita."
        />
      ) : (
        <div className="border-border divide-border divide-y rounded-lg border">
          {rows.map((document) => (
            <button
              key={document.id}
              type="button"
              className="hover:bg-muted/40 flex w-full flex-wrap items-center gap-3 p-3 text-left"
              onClick={() => setOpened(document)}
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium tabular-nums">{document.fullNumber}</p>
                <p className="text-muted-foreground text-xs">
                  {document.issueDate}
                  {document.customer === null ? '' : ` · ${document.customer.name}`}
                </p>
              </div>
              <span className="font-medium" data-numeric>
                {formatMoney(document.totalCents, document.currency)}
              </span>
              <Badge variant={document.kind === 'credit_note' ? 'warning' : 'neutral'}>
                {KIND_LABELS[document.kind]}
              </Badge>
              {/* Una fattura già rettificata lo dice qui: chi la cerca la cerca per questo. */}
              {document.correctedByDocumentId === null ? null : (
                <Badge variant="neutral">rettificata</Badge>
              )}
            </button>
          ))}
        </div>
      )}

      <p className="text-muted-foreground mt-4 text-xs">
        ⚠️ Questo registro <strong>non</strong> è l’elenco delle vendite: una vendita può non avere
        un documento, e una nota di credito non è una vendita. Le due somme coincidono solo quando
        ogni vendita è stata fatturata.
      </p>

      {opened === null ? null : (
        <DocumentDialog
          document={opened}
          onOpenChange={(open) => {
            if (!open) setOpened(null)
          }}
        />
      )}
    </>
  )
}

/**
 * Il dettaglio, e l'unico gesto che si può compiere su un documento: la **nota di credito**.
 *
 * Un documento fiscale è immutabile — non c'è una `PATCH` e non ci sarà — e si rettifica
 * emettendo una nota che lo nomina. Il documento rettificato **resta**: il registro non si
 * riscrive, ed è la forma dell'annullo di INV-03 e del registro del borsellino.
 *
 * ⚠️ La nota è **totale e non parziale**, e la schermata non lascia intendere il contrario:
 * niente importo da scrivere. Il caso vero è «la fattura è sbagliata», e si storna tutto e si
 * rifà.
 */
function DocumentDialog({
  document,
  onOpenChange,
}: {
  document: FiscalDocument
  onOpenChange: (open: boolean) => void
}) {
  const creditNote = useIssueCreditNote()
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [issued, setIssued] = useState<string | null>(null)

  // Il dettaglio porta **le righe**, che l'elenco non ha: `items` è l'unica differenza
  // fra le due risposte, ed è la ragione per cui questa rotta esiste. Finché non arriva
  // si mostra la riga d'elenco, che ha già numero, identità e totali — la finestra si
  // apre piena e si completa, invece di comparire vuota.
  const detail = useFiscalDocument(document.id)
  const data = detail.data ?? document

  const alreadyCorrected = data.correctedByDocumentId !== null
  const isCreditNote = data.kind === 'credit_note'

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{data.fullNumber}</DialogTitle>
          <DialogDescription>
            {KIND_LABELS[data.kind]} · emessa il {data.issueDate}
            {data.issuedByName === null ? '' : ` · ${data.issuedByName}`}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2 text-sm">
          <Amount label="Imponibile" cents={data.taxableCents} currency={data.currency} />
          <Amount label="IVA" cents={data.vatCents} currency={data.currency} />
          <Amount label="Totale" cents={data.totalCents} currency={data.currency} />
        </div>

        {/*
          Le due identità sono l'unica cosa che il documento **congela**: il cedente perché una
          palestra cambia sede e ragione sociale, il cessionario perché `sales` non porta
          l'identità dell'intestatario oltre l'id. Il nome del cessionario sopravvive
          all'anonimizzazione dell'anagrafica, e non è un difetto: è il punto in cui la
          conservazione decennale è la base legittima.
        */}
        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <p className="text-muted-foreground text-xs">Emesso da</p>
            <p>{data.seller.name}</p>
            {data.seller.vatNumber === null ? null : (
              <p className="text-muted-foreground text-xs">P. IVA {data.seller.vatNumber}</p>
            )}
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Intestato a</p>
            <p>{data.customer?.name ?? 'nessun intestatario'}</p>
            {data.customer?.taxCode == null ? null : (
              <p className="text-muted-foreground text-xs">{data.customer.taxCode}</p>
            )}
            {data.customer?.vatNumber == null ? null : (
              <p className="text-muted-foreground text-xs">P. IVA {data.customer.vatNumber}</p>
            )}
          </div>
        </div>

        {/* Le righe: la sola cosa che il dettaglio aggiunge all'elenco. Non sono una copia —
            vengono da `sale_items`, immutabili per privilegio —, quindi non possono divergere
            dai totali sopra. */}
        {detail.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : (data.items?.length ?? 0) === 0 ? null : (
          <div className="border-border divide-border divide-y rounded-md border text-sm">
            {(data.items ?? []).map((item) => (
              <div key={item.id} className="flex flex-wrap items-center gap-2 p-2">
                <span className="min-w-0 flex-1">
                  {item.quantity > 1 ? `${String(item.quantity)}× ` : ''}
                  {item.productName}
                </span>
                <span data-numeric>{formatMoney(item.lineTotalCents, data.currency)}</span>
              </div>
            ))}
          </div>
        )}

        {data.correctionReason === null ? null : (
          <div className="text-sm">
            <p className="text-muted-foreground text-xs">Motivo dello storno</p>
            <p>{data.correctionReason}</p>
          </div>
        )}

        {issued !== null ? (
          <Alert variant="success">Emessa la nota di credito {issued}.</Alert>
        ) : alreadyCorrected ? (
          <Alert variant="neutral">
            Questo documento è già stato rettificato da una nota di credito.
          </Alert>
        ) : isCreditNote ? null : confirming ? (
          <Alert variant="warning">
            <strong>La nota rettifica il documento per intero.</strong> Il documento resta nel
            registro e la nota lo nomina: il registro non si riscrive. Se la cifra è sbagliata si
            storna tutto e si rifà.
          </Alert>
        ) : null}

        {error === null ? null : <Alert variant="danger">{error}</Alert>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Chiudi
          </Button>
          {issued !== null || alreadyCorrected || isCreditNote ? null : confirming ? (
            <Button
              disabled={creditNote.isPending}
              onClick={() => {
                void (async () => {
                  setError(null)
                  try {
                    const note = await creditNote.mutateAsync({ documentId: document.id })
                    setIssued(note.fullNumber)
                    setConfirming(false)
                  } catch (caught) {
                    setError(
                      caught instanceof ApiError ? errorMessage(caught) : 'Emissione non riuscita.',
                    )
                  }
                })()
              }}
            >
              Conferma la nota di credito
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setConfirming(true)}>
              Emetti nota di credito
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Amount({ label, cents, currency }: { label: string; cents: number; currency: string }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="font-medium" data-numeric>
        {formatMoney(cents, currency)}
      </p>
    </div>
  )
}
